import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingShell } from '../components/OnboardingShell/OnboardingShell';
import { OnboardingV2Provider, useOnboardingV2 } from '../components/OnboardingShell/onboardingV2';
import { StepZeroFork } from '../components/onboarding/StepZeroFork';
import {
  clearForkChoice,
  readForkChoice,
  writeForkChoice,
  type ForkRole,
} from '../components/onboarding/forkStorage';
import { Step1Journey } from '../components/onboarding/steps/Step1Journey';
import { CoachOnboarding } from '../components/onboarding/CoachOnboarding';
import { NameStep } from '../components/onboarding/steps/NameStep';
import { Step2Location } from '../components/onboarding/steps/Step2Location';
import { Step3ParQ } from '../components/onboarding/steps/Step3ParQ';
import { Step4BodyMetrics } from '../components/onboarding/steps/Step4BodyMetrics';
import { Step5Pace } from '../components/onboarding/steps/Step5Pace';
import { Step6Focus } from '../components/onboarding/steps/Step6Focus';
import { Step7Welcome } from '../components/onboarding/steps/Step7Welcome';
import { GuardianInviteBeat } from '../components/onboarding/GuardianInviteBeat';
import { AgeStep } from '../components/onboarding/AgeStep';
import { AGE_STEP_COPY, isUnder13 } from '../lib/onboardingAgeCopy';
// B196 — device-local residue cleared on the stop screen's "Sign out" (the
// same B194 clearer the self-serve account-deletion flow uses). Not owned by
// this lane; only its existing export is called, never edited.
import { clearOnAccountDeletion } from '../lib/deviceState';
// B196 F4 — lane L3's client helper (src/app/src/lib/refusedAccount.ts),
// built in parallel in a separate worktree. Imported BY NAME per the spec's
// own instruction to this lane. It does not exist in this worktree, so
// `npm run build` and every test that mounts this route fail to resolve it
// until harvest lands L3's file alongside this one — expected and reported
// in the lane report, not worked around here (a dynamic `import()` was
// tried and rejected: Vite's import-analysis resolves it just as eagerly as
// a static one, so it fails the same way for every test that loads this
// file, with none of a static import's readability — spec v2 §7 L2:
// "Depends on L3's client helper by name only").
import { removeRefusedSignup, refusedSignupRemovalMessage } from '../lib/refusedAccount';
import { OnboardingLiteChapter } from '../components/onboarding/OnboardingLiteChapter';
import {
  deriveOnboardingLiteSteps,
  ONBOARDING_LITE_CHAPTER_COPY,
  type LiteStepId,
} from '../lib/onboardingLite';
import { armDualArrivalBanner } from '../components/membership/DualArrivalBanner';
import { useUserTier } from '../hooks/useUserTier';
// B196 L8 — the lite completion write (below) is the one completion-error
// branch this route owns; a server refusal of RULE 1 (the floor) reads the
// same ratified sentence + the same quiet line the rest of the app uses for
// a coded refusal, rather than the generic "Something went wrong" chip.
import { ReplanRefusalLine } from '../components/plan/ReplanRefusalLine';
import { editAgeBelowMinimum, AGE_BELOW_MINIMUM_MESSAGE } from '../lib/planEdit';

/** B76C — the lite steps that are actual QUESTIONS (the age step runs above
 *  the queue and is dispatched by the REL-29 block, not by `renderLiteStep`). */
type LiteQuestionId = Exclude<LiteStepId, 'age'>;
import { CollectionBeat } from '../components/firstPlan/CollectionBeat';
import {
  missingTrainingData,
  type DistanceUnit,
  type MissingItem,
} from '../lib/firstPlan/trainingData';
import { SerifHeading } from '../components/primitives';
import { useAuthContext } from '../contexts/AuthContext';
import {
  COMPETITIVE_JOURNEYS,
  JOURNEY_OPTIONS,
  STEP_META,
  type JourneyId,
} from '../fixtures/onboarding';
import {
  chapterByNumber,
  chapterProgressFor,
  deriveChapterFlow,
  wizardStepScreen,
  type ChapterProgress,
  type ChapterScreenId,
} from '../lib/onboardingChapters';
import { useAthleteContext } from '../hooks/useAthleteContext';
import { useOnboardingState } from '../hooks/useOnboardingState';
import { usePersistAccountRole } from '../hooks/usePersistAccountRole';
import { useRelationships } from '../hooks/useRelationships';
import { deriveAge, usePersistOnboarding } from '../hooks/usePersistOnboarding';
import { isGuardianBeatBand } from '../lib/guardianNudge';
import { onboardingCtaLabel } from '../lib/onboardingCtaCopy';
import { preselectUnit } from '../lib/onboardingUnits';
import { isNativeApp } from '../lib/platform';
import { useSettingsPersist } from '../lib/settingsPersist';
import { useServerFlag } from '../hooks/useServerFlag';
import { SIGN_IN_LANDING_PATH } from '../components/Landing/v2/landingContent';
import {
  clearPersistedState,
  type OnboardingAnswers,
  type StepNumber,
} from '../hooks/onboardingReducer';
import { track } from '../lib/analytics';
import { resetHorizontalScroll } from '../lib/resetHorizontalScroll';
import { scrollRouteToTop } from '../lib/scrollRouteToTop';

/**
 * Phase 2 onboarding route — `/v2/onboarding`.
 *
 * Reads URL params for preview state switching:
 *   - ?reset       — clear localStorage and restart at Step 1
 *   - ?step=N      — jump to step N (1-7), useful for previewing
 *                    branched Step 5/6 variants
 *   - ?journey=J   — preseed a journey (one of competitive_solo /
 *                    competitive_team / time_goal / health_fitness /
 *                    social) so Step 5+6 render the right branch
 *
 * Otherwise renders the localStorage-preserved state (or initial
 * state if none).
 *
 * Backend wiring is deferred to Phase 7 cutover. Phase 2 is preview-
 * only — state lives in localStorage at `wai:onboarding:v2:preview`.
 *
 * 2.0 app pass, Part 7 (ratified 2026-10-24): `onboarding_2_0_v1` restyles the
 * shell and every screen below. The flag is read ONCE, here, as a literal (the call
 * site `tests/config-flag-export-rake.test.js` reads, with the `/api/config` export
 * pinned by `tests/onboarding-2-0-wire.test.js`) and handed to the tree as context:
 * no other component reads it, and with it off nothing below renders differently.
 * Same screens, same order, same branches, same questions either way.
 */
export function Onboarding() {
  const onboarding2On = useServerFlag('onboarding_2_0_v1');
  return (
    <OnboardingV2Provider value={onboarding2On}>
      <OnboardingRoute />
    </OnboardingV2Provider>
  );
}

function OnboardingRoute() {
  const auth = useAuthContext();
  // Part 7: the answer the wrapper above read, for the few props that differ.
  const onboarding2On = useOnboardingV2();
  // Stamp the local wizard blob with the signed-in user id so a prior
  // (deleted) account's leftover state in this browser can't be adopted
  // by the Today migration — see loadPersistedStateForUser.
  const { state, setStep, nextStep, prevStep, setAnswer, reset } = useOnboardingState(
    auth.user?.id ?? null,
  );
  const [searchParams] = useSearchParams();
  const athleteContext = useAthleteContext();
  const navigate = useNavigate();
  const { persistAccountRole } = usePersistAccountRole();
  // P7(a) — the "Coaching a team?" fork only shows when the server enables
  // coach provisioning (flag coach_provisioning_v1). `coachPath` swaps the
  // journey wizard for the two-screen team-setup flow.
  const coachProvisioningOn = useServerFlag('coach_provisioning_v1');
  // B411 (harvest 2026-10-20 D1): read as a literal, above every early return. With it on (web) a signed-out
  // visitor goes to the landing with the sign-in sheet open, as on every other signed-out route; the landing's
  // Pricing "Start free" and "Get started" link here.
  const landing2On = useServerFlag('landing_2_0_v1');
  const [coachPath, setCoachPath] = useState(false);
  // REL-29 — the age step is answered-once-per-visit: once it hands off, a
  // mid-flow re-render must not bounce the athlete back to it.
  const [ageStepDone, setAgeStepDone] = useState(false);
  // B454 — Back from "Who is this account for?" (or from the name screen when
  // the fork is off) re-opens the age step with the pair she gave. The step is
  // otherwise answered-once-per-visit, so this is the only door back to it.
  const [ageReopened, setAgeReopened] = useState(false);
  // B211 — the most recent failed self-removal's sentence, for the stop
  // screen's own `removeError` prop (`AgeStep.tsx`). `null` on a fresh
  // mount and cleared to `null` at the start of every new attempt, so a
  // stale sentence from a prior tap never lingers under a currently-armed
  // confirm pair.
  const [removeSignupError, setRemoveSignupError] = useState<string | null>(null);
  // B215 — mirrors `AgeStep`'s own `removalInFlight`, reported via
  // `onRemovalInFlightChange` in the age-step mount block below, so the
  // shell's Sign out can be disabled for the same in-flight window as the
  // stop's own Back/Cancel/confirm. Declared here, alongside the sibling
  // state this same handler already owns, rather than inside the mount
  // block itself: a hook cannot be called conditionally (every render below
  // this point can still return early — auth loading, no session, a
  // completed-onboarding redirect — so a hook placed after those checks
  // would be skipped on some renders and violate the rules of hooks).
  const [removalInFlight, setRemovalInFlight] = useState(false);
  // ── O1 — THE NEW CHAPTER SCREEN (the name opener) ───────────────────────
  //
  // It has no wizard step NUMBER, deliberately. The name screen opens chapter
  // 1 ahead of step 1, so numbering it would have renumbered every step after
  // it — and the step number is what a resumed localStorage blob, `?step=`,
  // `prevStep` and `setStep(6)` are all written against. It is INTERPOSED
  // instead, the way this route already interposes the training-data beat and
  // the guardian beat: a flag says whether the screen has handed off, and the
  // render branch reads it. (The universal pace screen that used to sit in
  // chapter 3 was removed by ruling 3, 2026-10-22.)
  //
  // B454 — the flag is `answers.nameStepDone`, in the answer bag and so in the
  // persisted blob, not route state: the name screen and the journey question
  // share wizard step 1, and a reload on the journey question used to land on
  // the name screen again. Back from the journey question writes `false`, which
  // is what lets Back return to the name screen with her own answer still in
  // the field. It also keeps the QUEUE and what actually RENDERS in agreement,
  // which is the property the progress denominator depends on — a screen that
  // is counted is a screen that is shown.
  // ── B76C — ONBOARDING-LITE (2c → 2d → the reduced steps) ────────────────
  //
  // A coach-first account that has bought the athlete membership has answered
  // NOTHING about its own running, so it runs a REDUCTION of this same wizard:
  // the existing AgeStep, then the existing step components for the three
  // questions the CD handoff enumerates, then the existing atomic completion
  // write. No forked designs, no second reducer, no second persist — the only
  // new thing is which steps are queued (`lib/onboardingLite`).
  //
  // The queue is FROZEN when the chapter hands off rather than re-derived per
  // render, because two of the queued steps can legitimately be left blank
  // (pace is skippable, body metrics are optional) — a live re-derivation
  // would put the athlete back on a step they just declined, forever.
  const tier = useUserTier();
  const { persistOnboardingComplete } = usePersistOnboarding();
  const [liteChapterPassed, setLiteChapterPassed] = useState(false);
  const [liteQueue, setLiteQueue] = useState<LiteQuestionId[] | null>(null);
  const [liteCursor, setLiteCursor] = useState(0);
  const [liteSaving, setLiteSaving] = useState(false);
  const [liteError, setLiteError] = useState<string | null>(null);
  // B196 L8 — true only for the RULE 1 floor refusal (`age_below_minimum`)
  // on the completion write; renders the ratified sentence through
  // `ReplanRefusalLine` instead of `liteError`'s generic alert chip. The two
  // are mutually exclusive (see `finishLite`).
  const [liteHeld, setLiteHeld] = useState(false);
  // CG-7 — the fork only renders when the server reports relationships
  // enabled (flag relationships_v1): with the flag off, a guardian
  // choice would land on a blank /guardian. Server-driven, like every
  // other guardian surface.
  const rels = useRelationships();

  // B-15 — onboarding is the app's keyboard-heavy flow: WKWebView's
  // scroll-to-reveal-focused-input pan can survive the keyboard's dismissal
  // (native-layer scroll; the B-2 `overflow-x: clip` doesn't govern it) and
  // then survive the same-document SPA exit into /today — the beta-reported
  // left-shifted Today. Reset the horizontal axis exactly once as this route
  // unmounts, whatever the destination (/today, /guardian, /team).
  useEffect(() => () => resetHorizontalScroll(), []);

  // CG-7 — step-0 fork state. 'athlete' persists locally (refresh
  // doesn't re-ask); a guardian choice persists server-side and
  // navigates away, so it never needs the local flag. F-201: the fork
  // choice is keyed by auth user id, so a PRIOR account's leftover
  // choice in a shared browser can't skip the picker for a fresh signup.
  const [forkDone, setForkDone] = useState(
    () => readForkChoice(auth.user?.id ?? null) === 'athlete',
  );
  const [forkBusy, setForkBusy] = useState(false);
  const [forkError, setForkError] = useState<string | null>(null);
  // B456 — the highlighted who-for choice is held HERE, not inside the fork
  // screen. Every early `return null` below (auth settling, the relationships
  // read, the context read) unmounts the screen; its tap went with it, and the
  // next Continue landed on a screen that had forgotten the choice (walk
  // 2026-10-22, "the first Continue fails silently, the second works"). Route
  // state survives those returns, so the choice and a failed Continue's retry do.
  const [forkSelected, setForkSelected] = useState<ForkRole | null>(null);
  // B454 — Back from the name screen re-opens the fork (the account question
  // has already been answered, so `forkDone` alone would never show it again).
  const [forkReopened, setForkReopened] = useState(false);
  // B456 — a REFETCH is not a first load. `useRelationships` sets `loading`
  // back to true whenever its session or fetcher identity changes, and the
  // hold below used to treat that as "we cannot yet know" and unmount a fork
  // that was already on screen. The hold is for the first settle only.
  // Both are keyed by the signed-in user id so an in-place account switch
  // starts with a first load again.
  const relsSettledRef = useRef<string | null>(null);
  const contextSettledRef = useRef<string | null>(null);
  const settledUid = auth.user?.id ?? null;
  if (!rels.loading) relsSettledRef.current = settledUid;
  if (!athleteContext.loading) contextSettledRef.current = settledUid;
  const relsSettled = relsSettledRef.current === settledUid;
  const contextSettled = contextSettledRef.current === settledUid;
  // UAT-B2 — the 13–15 guardian-invite beat is interposed between the wizard's
  // last question and Welcome. `beatPassed` flips once the athlete skips OR
  // sends an invite and continues, so Welcome then renders. The beat never
  // re-mounts in-wizard after that.
  const [beatPassed, setBeatPassed] = useState(false);
  // F-520 — the SP-3 data beat (weekly mileage / experience / injury) now runs
  // IN the wizard, AFTER the last question step and BEFORE the guardian beat /
  // Welcome (founder: post-Welcome it read as surprise-extra-onboarding). Flips
  // once the three questions are answered; the answers are stashed on
  // `answers.trainingData` (persisted with the wizard blob) and folded into the
  // atomic completion POST at Step7 — so first-look then mounts straight to
  // generating (its data_missing collection beat stays as the legacy fallback).
  const [dataBeatPassed, setDataBeatPassed] = useState(false);
  // Round-21 critic F2 — THE BOUNCE FROM THE REVEAL IS ANNOUNCED.
  //
  // B108's reveal CTA sends an athlete whose screening is incomplete back to
  // step 3 rather than persisting a completion claim. Until now that arrival
  // said nothing: a disabled Continue (out of the tab order, announcing
  // nothing) was the only signal, and the page kept the reveal's scroll
  // offset, so the unanswered question could sit above the fold. The flag
  // carries "she arrived from the reveal" into step 3, which renders one
  // connective sentence for it; anything that moves her off step 3 clears it,
  // so a later pass through the screening reads as an ordinary step again.
  const [bouncedFromReveal, setBouncedFromReveal] = useState(false);
  const { saveField } = useSettingsPersist();
  const handleBeatSkip = async () => {
    try {
      // Non-blocking (the beat is never a gate): stamp the skip, but proceed
      // to Welcome regardless of whether the persist succeeds.
      await saveField('_guardian_invite_skipped_at', new Date().toISOString());
    } catch {
      /* best-effort — the Today resurface just won't have a stamp to key on */
    }
    setBeatPassed(true);
  };

  // The id settles null → real id after auth loads (lazy init above only
  // catches an already-known id, e.g. a remount). Pull THIS user's own fork
  // choice once it's known — a foreign/legacy value stays absent → picker.
  useEffect(() => {
    if (!forkDone && auth.user?.id && readForkChoice(auth.user.id) === 'athlete') {
      setForkDone(true);
    }
  }, [auth.user?.id, forkDone]);

  const onForkChoose = async (role: ForkRole) => {
    if (role === 'athlete') {
      writeForkChoice('athlete', auth.user?.id ?? null);
      setForkDone(true);
      setForkReopened(false);
      return;
    }
    // P7(a) — coach: open the two-screen team-setup overlay right here.
    // No persist and no local fork flag at choice time — CoachOnboarding
    // persists `_account_role='coach'` on FINISH; a cancel simply lands
    // back on this fork with nothing written.
    if (role === 'coach') {
      setCoachPath(true);
      return;
    }
    // Guardian: the intent MUST land server-side before leaving — a
    // lost write would drop this account back into athlete onboarding
    // on its next visit. Hard fail → inline error chip + retry.
    setForkBusy(true);
    setForkError(null);
    try {
      await persistAccountRole('guardian');
      navigate('/guardian', { replace: true });
    } catch {
      setForkError('Could not save your choice. Check your connection and try again.');
    } finally {
      setForkBusy(false);
    }
  };

  // Apply URL-param preview overrides ONCE (and only once — changing params
  // mid-flow shouldn't clobber typed state).
  //
  // B26 — gated on a settled session. `?reset` CLEARS the local wizard blob,
  // and React still runs this effect on the render that redirects a signed-out
  // visitor away (the auth gate below returns <Navigate>, which itself
  // navigates from an effect). Unguarded, a single /v2/onboarding?reset hit
  // with no session would destroy exactly the answers the same-browser rescue
  // exists to recover. No session → no preview override, nothing cleared.
  //
  // The ref keeps the original mount-only semantics across the auth-loading
  // renders: `auth.user` settles null → real id, so this cannot key off mount
  // alone without skipping the override entirely for signed-in admins.
  // ── B108 — `?step=` / `?journey=` ARE A DEV TOOL, and were a door ───────
  //
  // The comment on this effect called them "preview overrides" and the guard
  // below only asked for a session, so any signed-in visitor could open
  // `/v2/onboarding?step=7&journey=health_fitness` and land on the reveal with
  // a journey and no answers — a step-7 blob the completion CTA and the Today
  // migration branch would then persist. That is how an athlete reached full
  // quality authoring having never been screened (round-15 security 1). The
  // same two params ALSO suppress the deterministic age step below, so the URL
  // could skip the REL-29 age capture the clinical bands and the COPPA hold
  // read. Both are gated here, on one value.
  //
  // Dev builds only. An admin fact does exist on the client (`useAdminGate`,
  // `GET /api/admin/check`) but it is ASYNCHRONOUS, and this decision is made
  // on the FIRST render — the age step branches on these params before any
  // fetch could resolve — so honouring it for admins would mean either holding
  // the wizard's first paint on a network call for every athlete, or applying
  // the override a render late. Neither is worth a preview deep-link. `?reset`
  // is unchanged and still works for everyone: it destroys nothing that is not
  // this visitor's own local blob, and it is the escape hatch the completed-
  // onboarding redirect below already honours.
  const previewOverridesAllowed = import.meta.env.DEV;
  const previewStepParams =
    previewOverridesAllowed &&
    (searchParams.has('step') || searchParams.has('journey'));
  const previewAppliedRef = useRef(false);
  useEffect(() => {
    if (auth.loading || !auth.user || previewAppliedRef.current) return;
    previewAppliedRef.current = true;
    if (searchParams.has('reset')) {
      clearPersistedState();
      reset();
      return;
    }
    if (!previewOverridesAllowed) return;
    const stepParam = searchParams.get('step');
    if (stepParam) {
      const n = Number(stepParam);
      if (n >= 1 && n <= 7) setStep(n as StepNumber);
    }
    const journeyParam = searchParams.get('journey');
    if (journeyParam && JOURNEY_OPTIONS.some((o) => o.id === journeyParam)) {
      setAnswer({ journey: journeyParam as JourneyId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply-once, after auth settles
  }, [auth.loading, auth.user]);

  // Analytics: emit a step event whenever the step changes. Phase 2
  // ships with a no-op provider; Phase 7 wires a real SDK in
  // src/app/src/lib/analytics.ts (one-line provider swap).
  useEffect(() => {
    track('onboarding:step', { n: state.step, journey: state.answers.journey });
  }, [state.step, state.answers.journey]);

  // Round-21 critic F2 — the arrival line belongs to ONE arrival. Leaving
  // step 3 for anywhere but the reveal (Back, a `?step=` jump) retires it, so
  // a later visit to the screening reads as an ordinary step. Placed with the
  // other effects, above every early return (React error #300).
  //
  // Round-31 UX D2 — step 7 is the ONE exception, because it is the return
  // leg: the flag has to survive the hop it causes so the reveal knows she has
  // already watched its staging. It is retired the moment she leaves the
  // reveal backwards ("Change my answers" lands on step 6), so the settled
  // opening belongs to that one return and a later walk stages as before.
  useEffect(() => {
    if (state.step !== 3 && state.step !== 7) setBouncedFromReveal(false);
  }, [state.step]);

  // B150 (round-37 UX D1, reopened by the round-48 critic F1) — EVERY LEG INTO
  // STEP 7 LANDS AT THE TOP OF THE DOCUMENT.
  //
  // Step 7 is a SLOT, not a screen. For a fresh signup it renders the data beat
  // first (three questions, `dataBeatPending` below), then the 13-15 guardian
  // beat, and only then the reveal. The reset used to sit on ONE leg, step 6's
  // `nextStep`, and the comment there claimed a queued beat lands on the same
  // top of the same document. It does not: the beat advances its own questions
  // with `setIndex` and resets nothing, so the athlete who scrolled about 430px
  // down step 6 carried the BEAT's offset into the reveal, and the reveal
  // settled at maxScroll with "You're in" above the viewport. That is the exact
  // defect B150 filed, on the exact CTA it names: for a fresh signup "Meet my
  // coaches" sits on the beat's LAST question (`lib/onboardingCtaCopy.ts`), not
  // on step 6.
  //
  // The mechanism is scroll anchoring reacting to the reveal growing above her
  // after the 3.5s staging (round-37 measured scrollY 64 on the same walk with
  // `overflow-anchor: none`), so the reset belongs on the arrival, never on the
  // reveal itself.
  //
  // ONE reset rather than one per leg. The legs are `state.step` reaching 7
  // (step 6's Continue, the bounce's return from step 3, a resumed blob),
  // `dataBeatPassed` flipping (the beat's last question) and `beatPassed`
  // flipping (the guardian beat's Continue, and its Skip through
  // `handleBeatSkip`). Those three are the deps, so this runs once per arrival,
  // never on a re-render, and a fourth leg into the slot is covered by
  // construction rather than by remembering to copy three lines.
  //
  // LAYOUT, not passive: the reset has to commit in the same frame as the
  // screen it belongs to, or the athlete sees one frame of the new screen at
  // the old offset. The bounce's return leg keeps its own inline reset from
  // round-31 (it is not this lane's line) and the two agree; the OUTBOUND
  // bounce leg (reveal to step 3) is untouched, because it lands on step 3.
  useLayoutEffect(() => {
    if (state.step !== 7) return;
    // B181 — routed through the shared helper (lib/scrollRouteToTop.ts)
    // rather than a bare window.scrollTo; it carries its own guards and
    // deliberately preserves the horizontal position (B-15/B-2's axis).
    scrollRouteToTop();
  }, [state.step, dataBeatPassed, beatPassed]);

  // Phase 7 PR 1 — Safety guard: signed-in athlete who has already
  // completed onboarding (server flag set) shouldn't re-onboard. They
  // could land here via a stale URL, a bookmark, or a router push from
  // the Today gate before its server context loaded. Redirect to
  // /v2/today.
  //
  // Honors the `?reset` preview escape hatch — admins can still force
  // a reset to test the flow without clearing their own onboarding
  // state in the database.
  //
  // Path uses bare `/today` (NOT `/v2/today`) because React Router's
  // basename="/v2" prepends `/v2` automatically; passing `/v2/today`
  // would double-prefix to `/v2/v2/today`.
  //
  // CRITICAL — guard MUST come AFTER all hook calls. An early `return`
  // before the `useEffect`s above causes a "Rendered fewer hooks than
  // expected" React error (React error #300) on the second render
  // after athlete context loads (the first render runs all hooks
  // because loading=true → guard is false; the second render returns
  // early → useEffects skipped → hook count mismatch).
  // ── B26 — AUTH GATE AT THE WIZARD DOOR ──────────────────────────────────
  //
  // The wizard requires a session BEFORE it renders or collects anything.
  //
  // 2026-08-26, the first organic external user: a friend of the founder
  // reached here from the landing pricing CTA with NO session, answered every
  // question, met his coaches — and only the FINAL persist failed, on
  // `usePersistOnboarding`'s `if (!user) throw new Error('Not signed in')`.
  // The screen offered "Try again", a retry that could not succeed at any
  // point in the future, because nothing about clicking it creates a session.
  // Zero auth.users / public.users rows existed for the whole walkthrough. The
  // flow spent his entire first impression collecting answers it had no way to
  // keep.
  //
  // This guard is the door. It sits with the other post-hook redirects (see
  // the CRITICAL note below — every early return in this component must come
  // after all hook calls), and it is FIRST among them: no fork, no step, no
  // beat may render, and `useOnboardingState`'s persist effect may not write,
  // for a visitor with no session.
  //
  // Destination = the sign-in surface, mirroring routes/plan-first-look.tsx's
  // shipped `if (!auth.user) return <Navigate to="/landing" replace />`. Web
  // signs in on /landing (which hosts signInWithGoogle / signInWithApple);
  // inside the Capacitor shell that surface is /welcome (native-welcome),
  // which is where native-root already sends a signed-out launch.
  //
  // THE RETURN TARGET IS NOT A QUERY PARAM, deliberately. `signInWithGoogle`
  // sets `redirectTo` to a FIXED, Supabase-allow-listed `${origin}/v2/today`
  // that cannot vary per visitor — the exact dead end FIELD-TEST BUG 3 hit
  // when it tried to carry an invite code through OAuth (see
  // lib/team/joinStash.ts). Instead the return target is the Today gate, which
  // already owns this decision and is the one path that reads the rescue:
  //     signed out at /onboarding
  //       → /landing (or /welcome) → sign in → OAuth lands /v2/today
  //       → Today's onboarding gate reads localStorage for this user:
  //           · completed blob  → migrates the answers, stays on Today
  //                               (RESCUED — he is never re-asked)
  //           · partial blob    → /onboarding, wizard hydrates where he left off
  //           · no blob         → /onboarding at step 1
  // Every branch lands him in onboarding or past it, never on the marketing
  // page. Adding a second forwarding mechanism would only race that one.
  //
  // REL-29 SEAM (deterministic age capture) — NOW BUILT, at exactly the mount
  // this comment specified: below this guard, above the step-0 fork, so it runs
  // before any journey branch, and nothing else in this file moved. This gate
  // is the natural mount because it is the single point every
  // athlete-onboarding entrant passes through with a known session, which is
  // precisely what a deterministic age step needs (age capture used to be
  // incidental, via chat extraction — the reason five adult accounts had no age
  // until the 08-26 backfill). The step itself is `<AgeStep>`; its strings live
  // in lib/onboardingAgeCopy.ts and are PROVISIONAL pending ratification.
  if (auth.loading) {
    // Settle quietly — never flash step 1 at a visitor we are about to
    // redirect (same posture as the rels.loading / context-loading guards).
    return null;
  }
  if (!auth.user) {
    if (landing2On && !isNativeApp()) return <Navigate to={SIGN_IN_LANDING_PATH} replace />;
    return <Navigate to={isNativeApp() ? '/welcome' : '/landing'} replace />;
  }

  const isResetMode = searchParams.has('reset');
  const onboardingAlreadyComplete =
    !athleteContext.loading &&
    !!auth.user &&
    !!athleteContext.raw._onboarding_completed_at;
  if (onboardingAlreadyComplete && !isResetMode) {
    return <Navigate to="/today" replace />;
  }

  // CG-7 — a declared guardian account (server-side fork choice) never
  // re-enters athlete onboarding; their home is /guardian.
  const guardianIntent =
    !athleteContext.loading &&
    !!auth.user &&
    athleteContext.raw._account_role === 'guardian';
  if (guardianIntent && !isResetMode) {
    return <Navigate to="/guardian" replace />;
  }

  // P7(a) — a coach-provisioned account (the "Set up your team" fork) never
  // re-enters athlete onboarding; their home is /team.
  const coachIntent =
    !athleteContext.loading &&
    !!auth.user &&
    athleteContext.raw._account_role === 'coach';
  //
  // ⚖️ B76C — THE ONE EXCEPTION, and the entry to onboarding-lite.
  //
  // A coach who has BOUGHT the athlete membership is crossing to the athlete
  // side, and this route is the door. The condition is derived from state
  // rather than handed over by a purchase callback, deliberately: the same
  // three facts hold after a reload, after a StoreKit restore, after a
  // purchase made on another device, and after the web checkout redirect —
  // none of which run a callback in this tab. A callback would cover exactly
  // one of those paths and silently strand the rest.
  //
  // `onboardingAlreadyComplete` is checked above, so reaching here means this
  // account has not completed the athlete wizard.
  const membershipActive = tier.data ? tier.data.tier !== 'free' : false;
  const liteEligible = coachIntent && !isResetMode && membershipActive;
  if (coachIntent && !isResetMode) {
    // Hold while the membership fact resolves. Bouncing a coach who just
    // bought to /team and then yanking them into the lite flow is exactly the
    // role flash `useRoleGate` exists to prevent.
    if (tier.loading) return null;
    if (!liteEligible) return <Navigate to="/team" replace />;
  }

  /**
   * ⚖️ REL-29 — the deterministic AGE step, ABOVE the step-0 fork.
   *
   * Shown to a signed-in athlete who has not answered it yet. It writes
   * `answers.birthYear` / `answers.birthMonth`, the same two keys Step 3 sets
   * and the same two the coppa gate reads (`_birth_year` / `_birth_month` /
   * `Age` after `usePersistOnboarding` projects them), so nothing new lands on
   * the context and Step 3 hides its own birth row once they are present.
   *
   * ⛔ It does NOT intercept a completed athlete, a guardian or a coach: those
   * three redirects run above this. And it is skipped in reset mode and on the
   * preview deep-links, so `?step=` / `?journey=` behave exactly as before.
   *
   * B196 (F3) — `birthPairUnder13` is an ADDED disjunct, not a rewrite: a
   * persisted or in-memory pair that resolves under 13 makes this step due
   * unconditionally, so a stale/legacy blob (or a device that had an older
   * build open) can never skip straight into the wizard on `ageAnswered`
   * alone. `AgeStep` itself never derives its own "stop" display from these
   * props again once it has shown one — see that file's header — so clearing
   * them below (`onUnder13Detected`) cannot flip the screen back underneath
   * the athlete.
   */
  const ageAnswered =
    typeof state.answers.birthYear === 'number' && typeof state.answers.birthMonth === 'number';
  const birthPairUnder13 =
    typeof state.answers.birthYear === 'number' &&
    typeof state.answers.birthMonth === 'number' &&
    isUnder13(state.answers.birthYear, state.answers.birthMonth);
  // B76C — in the lite flow the age step is UNCONDITIONAL ("No upgrade path
  // may skip this step"); see lib/onboardingLite for why. Everywhere else this
  // is byte-for-byte the REL-29 condition: `liteEligible` is false, so the
  // expression reduces to `!ageAnswered && !ageStepDone && …`.
  // B108 — the last clause below is `!previewStepParams`, written out: the
  // preview deep-links may skip this step only in a DEV build. Age is a
  // clinical input, and a URL any signed-in athlete could type was a second,
  // quieter way to arrive unaged. (Spelled out rather than named because
  // `AgeStep.rel29.test.tsx` pins both literals inside the first 400
  // characters of the expression; the rule itself is the one value above.)
  // B453/B454 — two added disjuncts' worth of change, neither a rewrite: the
  // context-loading clause reads `contextSettled` (a REload of the athlete
  // context no longer unmounts a picker the athlete is in the middle of), and
  // `ageReopened` is Back from the next screen. The pair is only ever
  // committed by the age step's Continue (AgeStep.tsx), so `ageAnswered`
  // turning true is the hand-off, never a pick.
  const ageStepDue =
    ((liteEligible || !ageAnswered) &&
      !ageStepDone &&
      (!athleteContext.loading || contextSettled) &&
      (!previewOverridesAllowed ||
        (!searchParams.has('step') && !searchParams.has('journey')))) ||
    birthPairUnder13 ||
    ageReopened;
  if (ageStepDue) {
    return (
      <OnboardingShell
        current={1}
        metaOverride={{
          stepLabel: AGE_STEP_COPY.stepLabel,
          label: AGE_STEP_COPY.shellLabel,
          context: AGE_STEP_COPY.shellContext,
          ambientBg: STEP_META[0].ambientBg,
        }}
        signOutDisabled={removalInFlight}
      >
        <AgeStep
          birthYear={state.answers.birthYear}
          birthMonth={state.answers.birthMonth}
          onChange={(patch) => setAnswer(patch)}
          onContinue={() => {
            setAgeStepDone(true);
            setAgeReopened(false);
          }}
          onRemovalInFlightChange={setRemovalInFlight}
          onUnder13Detected={() => {
            // B196 F3 — the persisted blob goes. Deliberately just this, not
            // ALSO a `setAnswer` clearing the reducer's in-memory copy: that
            // dispatch would itself be a state change, and
            // `useOnboardingState`'s own every-change autosave (a file this
            // lane does not own) would re-persist right behind it with an
            // empty-but-present blob rather than none at all. Nothing on the
            // stop screen dispatches another SET_ANSWER afterward (its two
            // controls are the way back, which is local component state,
            // and removal, which never touches the reducer), so this one
            // call is the whole fix, not a race against anything.
            clearPersistedState();
          }}
          onRemoveUnder13={() => {
            // B196 F4 — a refused signup removing itself: no established
            // 13+ band, no guardian, no data. `removeRefusedSignup` is L3's
            // client helper (imported above); a failed removal leaves the
            // stop on screen.
            //
            // B211 — a failed removal used to be silently swallowed here
            // (`if (result.ok)` had no `else`). The refusal now surfaces
            // through `removeSignupError`, mapped to a sentence by
            // `refusedSignupRemovalMessage` (`lib/refusedAccount.ts`) — the
            // ONE place the two server sentences and the one bounded 429
            // sentence live, so this handler never invents a fourth.
            setRemoveSignupError(null);
            void (async () => {
              const result = await removeRefusedSignup();
              if (result.ok) {
                await clearOnAccountDeletion(auth.user?.id ?? null);
                await auth.signOut();
                return;
              }
              setRemoveSignupError(refusedSignupRemovalMessage(result));
            })();
          }}
          removeError={removeSignupError}
        />
      </OnboardingShell>
    );
  }

  /**
   * B76C · 2d + the reduced steps — ONBOARDING-LITE, below the age step and
   * above the step-0 fork.
   *
   * Position matters as much as it does for REL-29: the age step must precede
   * this (it does — the block above returns first), and the step-0 role fork
   * must never render for an account whose role is already settled, which is
   * what putting this above `forkEligible` guarantees.
   *
   * The steps themselves are the EXISTING components, the state is the
   * EXISTING reducer, and the completion is the EXISTING atomic write, so this
   * branch owns exactly one thing: the order.
   */
  if (liteEligible) {
    // Derived fresh for the card; frozen into `liteQueue` at hand-off. The age
    // step has already run by the time we get here, so it is not listed.
    const queued = deriveOnboardingLiteSteps(state.answers).filter(
      (s): s is LiteQuestionId => s !== 'age',
    );

    /**
     * The crossing. The SAME atomic completion POST the full wizard fires at
     * Step 7 — same keys, same validation, same server contract — then the
     * arrival banner is armed for THIS account and the athlete lands on Today.
     *
     * Always called from an event handler, never from render.
     */
    const finishLite = async () => {
      if (liteSaving) return;
      setLiteSaving(true);
      setLiteError(null);
      setLiteHeld(false);
      try {
        await persistOnboardingComplete(state.answers);
        // 2e — armed by the CROSSING rather than derived from the role, so an
        // account that was already dual before B76C is never told it has just
        // arrived somewhere it has been for months.
        armDualArrivalBanner(auth.user?.id ?? null);
        navigate('/today', { replace: true });
      } catch (err) {
        // B196 L8 (spec §4.8) — the RULE 1 floor refuses the completion
        // write itself (an established or preserved under-13 age); nothing
        // was saved. Render the ratified sentence, not the server's own
        // message, and leave the athlete exactly where she was — the same
        // `onBack` she already has is her way back, nothing further persists.
        const code =
          err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined;
        const held = editAgeBelowMinimum(typeof code === 'string' ? { code } : null);
        setLiteHeld(held);
        setLiteError(held ? null : err instanceof Error ? err.message : 'Something went wrong');
        setLiteSaving(false);
      }
    };

    if (!liteChapterPassed) {
      return (
        <OnboardingShell
          current={1}
          metaOverride={{
            stepLabel: ONBOARDING_LITE_CHAPTER_COPY.kicker,
            label: ONBOARDING_LITE_CHAPTER_COPY.headline,
            // Part 7: the intro prints its own serif headline and body, so under the
            // restyle the header keeps the kicker alone (frame 1ap).
            context: onboarding2On ? '' : ONBOARDING_LITE_CHAPTER_COPY.body,
            ambientBg: STEP_META[0].ambientBg,
          }}
          headerNameInBody
        >
          <OnboardingLiteChapter
            steps={queued}
            onContinue={() => {
              // A coach who somehow has every answer already crosses straight
              // over rather than being shown an empty flow.
              if (queued.length === 0) {
                void finishLite();
                return;
              }
              setLiteQueue(queued);
              setLiteCursor(0);
              setLiteChapterPassed(true);
            }}
          />
          {liteHeld ? (
            <ReplanRefusalLine message={AGE_BELOW_MINIMUM_MESSAGE} />
          ) : (
            liteError && (
              <p role="alert" style={LITE_ERROR_STYLE}>
                {liteError}
              </p>
            )
          )}
        </OnboardingShell>
      );
    }

    const flow = liteQueue ?? queued;
    const current = flow[liteCursor];
    // Defensive only: the cursor is only ever advanced by `advance`, which
    // finishes instead of running off the end.
    if (!current) return null;

    const advance = () => {
      if (liteCursor + 1 >= flow.length) {
        void finishLite();
        return;
      }
      setLiteCursor(liteCursor + 1);
    };

    return (
      <OnboardingShell
        current={1}
        onBack={liteCursor > 0 ? () => setLiteCursor(liteCursor - 1) : undefined}
        // Pace is the one step an athlete may pass without answering, per 2d.
        optional={current === 'pace'}
        // ⚖️ O1 — the lite flow is NOT the four-chapter athlete flow, so it
        // takes NO `chapter` prop: no segments, and the "Chapter 1 of 2"
        // placeholder kicker stays exactly what B76C shipped. Its own chapter
        // map "lands later" (see lib/onboardingLite's header) and inventing one
        // here would put a second, contradictory count on screen.
        //
        // The metaOverride is the same ratified 2d copy the chapter card above
        // renders, carried onto the question screens it introduced. Before O1
        // these screens fell through to STEP_META and wore "Step 1 of 6" plus
        // the six-dot rail — a count from a flow this athlete is not walking,
        // and a rail that no longer exists.
        metaOverride={{
          stepLabel: ONBOARDING_LITE_CHAPTER_COPY.kicker,
          label: ONBOARDING_LITE_CHAPTER_COPY.headline,
          context: ONBOARDING_LITE_CHAPTER_COPY.body,
          ambientBg: STEP_META[0].ambientBg,
        }}
      >
        {renderLiteStep(current, state.answers, setAnswer, advance)}
        {liteHeld ? (
          <ReplanRefusalLine message={AGE_BELOW_MINIMUM_MESSAGE} />
        ) : (
          liteError && (
            <p role="alert" style={LITE_ERROR_STYLE}>
              {liteError}
            </p>
          )
        )}
      </OnboardingShell>
    );
  }

  // CG-7 — step-0 fork: only for a signed-in FRESH account (no wizard
  // progress, no completion) entering normally, and only while the
  // relationships feature is server-enabled. Preview deep-links
  // (?step= / ?journey=) and mid-flow refreshes skip it.
  // B454 — `forkReopened` is Back from the name screen: the fork shows again
  // whatever `forkDone` and the journey say. B456 — and the context read counts
  // as settled once it has settled once (`contextSettled`): a later reload
  // flips `loading` back on for a moment, which used to drop the fork for the
  // name screen and back again under the athlete's thumb.
  const forkEligible =
    forkReopened ||
    (!forkDone &&
      !!auth.user &&
      (!athleteContext.loading || contextSettled) &&
      state.step === 1 &&
      !state.answers.journey &&
      // B108 — one gate for every preview deep-link escape in this file.
      !previewStepParams);
  if (forkEligible && rels.loading && !relsSettled) {
    // Settle quietly — don't flash Step 1 and then swap in the fork. First
    // load only (B456): a refetch must not unmount a fork already on screen.
    return null;
  }

  // P7(a) A1 chrome conformance — the coach path REPLACES the wizard body
  // inside the same step chrome (StepLayout primitives / OnboardingShell),
  // instead of overlaying the team-page modal/sheet. Reached from the step-0
  // fork's "I'm the coach" card or Step 1's secondary link; Back lands on
  // whichever screen the coach came from with nothing written.
  if (coachPath) {
    return (
      <OnboardingShell
        current={1}
        metaOverride={{
          stepLabel: 'Coach setup',
          label: 'Your team',
          context: 'Name your team and invite your athletes. They join by email, no codes.',
          // Part 7 (frames 1an, 1ao): the coach setup wears chapter 2's tint.
          ambientBg: onboarding2On ? chapterByNumber(2).ambientBg : STEP_META[1].ambientBg,
        }}
      >
        <CoachOnboarding
          onCancel={() => setCoachPath(false)}
          // B209 — the age step (above, REL-29) always runs before this
          // fork can render, so the wizard's birth pair is already
          // answered; passing it lets CoachOnboarding persist it to the
          // server before its first team create, so the unknown-age wall
          // never has to fall through to the coach exemption.
          birthYear={state.answers.birthYear}
          birthMonth={state.answers.birthMonth}
        />
      </OnboardingShell>
    );
  }

  if (forkEligible && rels.enabled) {
    return (
      <OnboardingShell
        current={1}
        // B454 — Back is the age step, with her pair kept. The age step itself
        // is the first screen and keeps no Back: nothing precedes it.
        onBack={() => setAgeReopened(true)}
        metaOverride={{
          stepLabel: 'Getting started',
          label: 'Welcome to Tayro',
          context: 'Tell us who this account is for. It shapes everything that follows.',
          ambientBg: STEP_META[0].ambientBg,
        }}
      >
        <StepZeroFork
          onChoose={onForkChoose}
          busy={forkBusy}
          error={forkError}
          coachOption={coachProvisioningOn}
          selected={forkSelected}
          onSelect={setForkSelected}
        />
      </OnboardingShell>
    );
  }

  // ── B45 — WHAT IS STILL QUEUED BEFORE THE COACHES REVEAL ────────────────
  //
  // Founder report (twice, on device): step 6 ("How does running fit into your
  // week?") offered "Meet my coaches" while another question still followed.
  // The label was hardcoded in Step6Focus under a comment calling step 6 "the
  // last substantive step before the Welcome outcome screen" — true when
  // written, untrue since F-520 inserted the training-data beat between step 6
  // and Welcome. A fresh signup is missing all three items, so the reveal was
  // announced one to three questions early, every time.
  //
  // "Which screen is last" is a property of THIS FLOW for THIS athlete, and it
  // moves whenever a beat is inserted, so no step component may decide it. The
  // two flags below are derived from the very state the render branches use
  // (see the beat blocks that follow), then handed to `onboardingCtaLabel` —
  // the label therefore cannot disagree with what actually renders next.
  //
  // Both are computed HERE, above the beat branches, because the data beat has
  // to know whether the guardian beat follows it in order to label its own last
  // question. The guardian derivations moved up with them; nothing about the
  // guardian beat's own render conditions changed.
  const beatAge =
    typeof state.answers.birthYear === 'number' &&
    typeof state.answers.birthMonth === 'number'
      ? deriveAge(state.answers.birthYear, state.answers.birthMonth)
      : null;
  const inBeatBand = isGuardianBeatBand(beatAge);
  const alreadySkipped = !!athleteContext.raw._guardian_invite_skipped_at;
  const hasAnyRelationship = rels.relationships.length > 0;

  // F-520 — the in-wizard data beat. Reaches step 7 (the post-question slot) →
  // ask the training-data items still MISSING from context (a fresh signup: all
  // three), BEFORE the guardian beat and Welcome. Answered → stash the canonical
  // patch on answers.trainingData + flip dataBeatPassed, so the flow falls
  // through to the guardian beat (minor) / Welcome; the answers ride the atomic
  // completion POST at Step7 (buildOnboardingPayload). Reuses the SAME first-look
  // CollectionBeat (verbatim dataGate copy) — Q1 Back returns to step 6, and the
  // last CTA is labelled from the flow (B45), not fixed: generation still happens
  // post-Welcome, in first-look, so it never says "Build my plan" here.
  const missingTD = missingTrainingData(athleteContext.raw);
  const dataBeatPending =
    !dataBeatPassed && !hasWizardTrainingData(state.answers) && missingTD.length > 0;
  // While relationships load we cannot yet know, so assume the beat IS coming:
  // the conservative guess is the one that says "Continue" and never promises a
  // reveal that another screen then delays.
  const guardianBeatPending =
    inBeatBand &&
    !alreadySkipped &&
    !beatPassed &&
    (rels.loading || (rels.enabled && !hasAnyRelationship));
  const pendingBeats = { dataBeatPending, guardianBeatPending };

  // ── O1 — THE CHAPTER FLOW, DERIVED FROM THE SAME FACTS ──────────────────
  //
  // CD handoff `design_handoff_onboarding_chapters` (founder-ratified
  // 2026-08-30): four named chapters, and a four-segment progress treatment
  // whose "fill denominator is per-athlete: computed by the route from the
  // screens ACTUALLY queued (same source of truth as the B45 pending-beats
  // flags), so conditional beats never make the bar lie."
  //
  // That sentence is why this sits HERE, immediately under `pendingBeats`,
  // reading the very same derivations. `missingTD.length` is the beat's real
  // length for this athlete, `guardianBeatPending` is the flag the beat's own
  // render branch uses, and `COMPETITIVE_JOURNEYS` is the predicate
  // `Step5Pace` itself branches on. Nothing here re-derives a fact; a second
  // derivation is exactly how B45's bug happened.
  //
  // ⛔ CHAPTER NAMES NEVER REACH A CTA. B45 owns every forward label and still
  // computes both of them below, from `pendingBeats`, unchanged.
  const journeyId: JourneyId = state.answers.journey ?? 'health_fitness';
  const competitiveJourney = COMPETITIVE_JOURNEYS.includes(journeyId);
  // Preview deep-links skip chapter 1's opener, exactly as they skip the age
  // step — `?step=` / `?journey=` exist to land on a specific branch.
  const nameStepQueued = !previewStepParams;
  const chapterFlow = deriveChapterFlow({
    nameStepQueued,
    competitiveJourney,
    // B465 — the beat's questions stay in the count once the beat has been
    // answered, so "Change my answers" does not shrink chapter 3 from "2 of 5" to
    // "2 of 2" (the same list `missingTD` the beat asked, so the denominator and
    // what was shown still agree).
    dataBeatQuestions:
      dataBeatPending || dataBeatPassed || hasWizardTrainingData(state.answers)
        ? missingTD.length
        : 0,
    guardianBeatQueued: guardianBeatPending,
  });
  /** Where a screen sits for THIS athlete, for the shell's `chapter` prop. */
  const chapterAt = (screen: ChapterScreenId): ChapterProgress | undefined =>
    chapterProgressFor(chapterFlow, screen) ?? undefined;

  // ── O1 · CHAPTER 1 OPENER — "What should we call you?" ──────────────────
  //
  // Sits above step 1 (the journey question) and below the doorstep, so the
  // age step and the account fork still run first with their frozen chrome.
  // Skippable in both directions: an empty Continue and "Skip for now" write
  // the same recorded skip (`displayName: ''`), because a minor walks this
  // flow and no new question may become a wall.
  //
  // B454 — Back is the account question when the fork is part of this flow
  // (the choice is forgotten locally so a reload there asks again, and the
  // screen keeps the athlete highlighted), otherwise the age step. Continue and
  // Skip write `nameStepDone`, so a reload on the journey question stays there.
  const nameStepDone = state.answers.nameStepDone === true;
  const nameStepDue = nameStepQueued && !nameStepDone && state.step === 1;
  if (nameStepDue) {
    return (
      <OnboardingShell
        current={1}
        onBack={() => {
          if (rels.enabled) {
            clearForkChoice(auth.user?.id ?? null);
            setForkSelected('athlete');
            setForkDone(false);
            setForkReopened(true);
          } else {
            setAgeReopened(true);
          }
        }}
        chapter={chapterAt('name')}
      >
        <NameStep
          answers={state.answers}
          setAnswer={setAnswer}
          onContinue={() => setAnswer({ nameStepDone: true })}
          authUser={auth.user}
          contextData={athleteContext.raw}
        />
      </OnboardingShell>
    );
  }

  // B457 — the unit the weekly question starts in: her own pick, else the
  // location country's km preselect (never miles-by-assumption written as hers).
  // B472 — and after that km when she chose kg and cm on the body step (`bodyUnit`
  // is written only by her tap on that toggle, so it is hers, never a default):
  // the question used to open in miles for an athlete who had just answered in
  // metric. Still a start, not an answer: nothing is saved until she answers.
  const weeklyUnit: DistanceUnit =
    state.answers.distanceUnit ??
    preselectUnit(state.answers.manualCountry) ??
    (state.answers.bodyUnit === 'metric' ? 'km' : undefined) ??
    'mi';
  const dataBeatCandidate = state.step === 7 && dataBeatPending;
  // Settle quietly while context loads — never ask for data context already
  // holds (mirrors the guardian beat's rels.loading guard just below). B456 —
  // first load only: a reload of the context must not unmount a beat the
  // athlete is answering (its answers live in the beat until it completes).
  if (dataBeatCandidate && athleteContext.loading && !contextSettled) {
    return null;
  }
  if (dataBeatCandidate) {
    return (
      <CollectionBeat
        missing={missingTD as MissingItem[]}
        // O1 — the beat is the tail of CHAPTER 3, not a screen outside the
        // chapters, so it wears chapter 3's chrome and its questions are
        // counted in chapter 3's denominator. This is the position of the
        // beat's FIRST question; the beat offsets by its own index.
        chapter={chapterAt('data_beat_1')}
        // B150 — the leg every fresh signup walks into the reveal. It needs no
        // scroll reset of its own: `dataBeatPassed` is a dep of the layout
        // effect above, so flipping it lands the next step-7 screen at the top.
        // B457 — the weekly question is asked, and its bands shown, in the unit
        // she chose (else the location preselect, else miles). Completing the
        // beat saves the unit it was answered in as `distanceUnit`, which the
        // completion payload writes as `distance_unit` so the plan and Today
        // read it back. Only when the weekly question was actually asked.
        distanceUnit={weeklyUnit}
        onDistanceUnitChange={(unit) => setAnswer({ distanceUnit: unit })}
        onComplete={(patch) => {
          setAnswer({
            trainingData: patch,
            ...(missingTD.includes('current weekly mileage') ? { distanceUnit: weeklyUnit } : {}),
          });
          setDataBeatPassed(true);
        }}
        onBackFromFirst={() => setStep(6)}
        // B45 — the beat's LAST question is the last question before the
        // coaches reveal for most athletes, so this is where "Meet my coaches"
        // belongs. It stays "Continue" when the guardian beat still follows.
        lastCtaLabel={onboardingCtaLabel('data_beat_last_question', pendingBeats)}
      />
    );
  }

  // UAT-B2 — the 13–15 guardian-invite beat. Mounts AFTER the wizard's last
  // question (step 6) and BEFORE Step7Welcome (step === 7), for a fresh 13–15
  // athlete with ZERO guardian relationships who hasn't already skipped. The
  // minor signal uses the SAME conservative birthday-month rule the server
  // derivation applies (`deriveAge`, mirroring clinicalModifiers.deriveAgeFromBirth)
  // on the birth fields the wizard just captured — never ad-hoc client age math,
  // and never blocking (Skip / Continue / Sign out are same-weight paths).
  //
  // B45 — `beatAge` / `inBeatBand` / `alreadySkipped` / `hasAnyRelationship` are
  // declared above (with the queued-beat flags) because the data beat needs them
  // to label its own last question. The conditions below are unchanged.
  const beatCandidate =
    state.step === 7 && inBeatBand && !alreadySkipped && !beatPassed;
  // Settle quietly while relationships load, so we never flash Welcome then
  // swap in the beat (mirrors the step-0 fork's rels.loading guard).
  if (beatCandidate && rels.loading) {
    return null;
  }
  if (beatCandidate && rels.enabled && !hasAnyRelationship) {
    return (
      <OnboardingShell
        current={7}
        onBack={() => setStep(6)}
        optional
        // O1 — the beat sits IN chapter 4 and shows its segments, but keeps
        // its own ratified framing: it is a policy surface, not a numbered
        // question, and the handoff places it in chapter 4 "unnumbered,
        // unchanged posture". `metaOverride` therefore still owns the kicker,
        // the title, the context line and the signal-soft ambient — exactly as
        // the ratified simulation renders it — while `chapter` supplies the
        // segments. `fillOverride` is the simulation's own 40 percent: there is
        // no question position here to compute a fill from.
        chapter={{ n: 4, questionIndex: null, questionCount: 0, fillOverride: 0.4 }}
        metaOverride={{
          stepLabel: 'One more thing',
          label: 'A parent in your corner',
          context: 'Recommended at your age. Never required.',
          ambientBg: 'var(--signal-soft)',
        }}
        // Part 7: the parent beat prints the name as its serif headline (frame 1ag).
        headerNameInBody
      >
        <GuardianInviteBeat
          age={beatAge as number}
          onSkip={() => void handleBeatSkip()}
          // B150 — same as the data beat: `beatPassed` is a dep of the layout
          // effect above, so both this and Skip land the reveal at its top.
          onContinue={() => setBeatPassed(true)}
        />
      </OnboardingShell>
    );
  }

  // Render the appropriate step body. Each step component owns its
  // CTA + validation; the shell only owns the chrome.
  const stepBody = renderStep(
    state.step,
    state.answers,
    setAnswer,
    // Round-29 critic 1 — THE BOUNCE RETURNS WHERE IT CAME FROM. The arrival
    // line promises the dashboard is ready once the seven answers are in; with
    // `nextStep` here she then walked steps 4, 5 and 6 and sat through the
    // reveal's staging again before reaching it, and the flag cleared the
    // instant the step left 3, so nothing on those screens explained the
    // detour. The flag is only ever true on step 3 and on the reveal it
    // returns to (the effect above retires it everywhere else), so this reads:
    // the ONE arrival that came from the reveal goes back to the reveal, and
    // every ordinary pass through the screening walks on as before.
    bouncedFromReveal
      ? () => {
          setStep(7);
          // Round-31 UX D1 — and it returns her to the TOP of the reveal. The
          // outbound leg below already does this; without the mirror the
          // browser kept step 3's offset (a 1764-1849px screen) on the
          // reveal (1113-1238px) and clamped her to its bottom, so at 320 she
          // arrived past "You're in" with the CTA at y=96.
          // B181 — routed through the shared helper.
          scrollRouteToTop();
        }
      : // B150 — step 6's forward leg needs nothing of its own any more: the
        // `useLayoutEffect` above resets the page for every arrival at step 7,
        // this one included. Its scroll-anchoring history is recorded there.
        nextStep,
    coachProvisioningOn ? () => setCoachPath(true) : undefined,
    // B45 — step 6's CTA names the coaches ONLY when the reveal is next.
    onboardingCtaLabel('step6_focus', pendingBeats),
    // O3 — "Change my answers" on the reveal. It re-enters the wizard at the
    // last question and MUST NOT persist: the atomic completion POST rides the
    // dashboard CTA inside Step7Welcome, and this link has to run before it.
    //
    // ⚖️ B45 falls out for free here, which is the point of the label staying
    // route-computed. Coming back to step 6 re-runs the expression above with
    // both beats already passed, so "Meet my coaches" reappears exactly when
    // the reveal really is the next screen. Nothing is cached across the reveal
    // boundary because there is nothing to cache.
    () => setStep(6),
    // B108 — the reveal's dashboard CTA refuses to complete an onboarding
    // whose PAR-Q screening is not complete, and sends the athlete to the
    // question instead of persisting a payload that claims it happened. Step 3
    // is the screening step, and it asks in the wizard's own ratified copy.
    () => {
      setBouncedFromReveal(true);
      setStep(3);
      // Round-21 critic F2 — she scrolled to the reveal's CTA to get here;
      // step 3 is a different, shorter screen and would otherwise open at
      // that same offset, clamped near its bottom.
      // B181 — routed through the shared helper.
      scrollRouteToTop();
    },
    bouncedFromReveal,
  );

  // Step 7 Welcome opts out of the standard chrome via `bare`.
  const isWelcome = state.step === 7;

  return (
    <OnboardingShell
      current={state.step}
      // B454 — Back from the journey question returns to the name screen with
      // what she typed (it is hidden only when the name screen is not part of
      // this flow, the dev preview deep-links). Everywhere else `prevStep` is
      // unchanged.
      onBack={
        state.step === 1 && nameStepQueued
          ? () => setAnswer({ nameStepDone: false })
          : state.step > 1 && state.step < 7
            ? prevStep
            : undefined
      }
      optional={state.step === 4}
      bare={isWelcome}
      chapter={chapterAt(wizardStepScreen(state.step, competitiveJourney))}
    >
      {stepBody}
    </OnboardingShell>
  );
}

/**
 * F-520 — has the in-wizard data beat already recorded the training-data
 * answers? Stored as the canonical patch on `answers.trainingData` (persisted
 * with the wizard blob), so a refresh at step 7 re-hydrates it and the beat is
 * skipped rather than re-asked.
 */
function hasWizardTrainingData(answers: OnboardingAnswers): boolean {
  const td = answers.trainingData;
  return !!td && typeof td === 'object' && Object.keys(td).length > 0;
}

/** B76C — the lite flow's save-failure chip. Inline, like the `Unknown step`
 *  fallback below: one line on one screen does not earn a CSS module. */
const LITE_ERROR_STYLE = {
  marginTop: 12,
  fontSize: 'var(--ramp-bridge-13)',
  color: 'var(--danger, #B43D3D)',
} as const;

/**
 * B76C · 2c–2d — the lite flow's step dispatcher.
 *
 * Deliberately a sibling of `renderStep` rather than a branch inside it: the
 * lite flow addresses steps by NAME (the queue is a list of `LiteStepId`)
 * while the wizard addresses them by NUMBER, and collapsing the two would put
 * a role fork inside the step machine that every athlete pays for.
 *
 * The components are the wizard's own, unmodified. None of them is handed a
 * CTA label, and that is the point: B45's rule is that only the flow may name
 * the coaches reveal, and no lite screen is ever the screen before it (pinned
 * in `onboardingLite.b76c.test.ts` via `liteStepCtaScreen`). They each render
 * their own "Continue", which is what B45 resolves for every one of them.
 */
function renderLiteStep(
  step: LiteQuestionId,
  answers: OnboardingAnswers,
  setAnswer: (patch: Partial<OnboardingAnswers>) => void,
  onContinue: () => void,
) {
  if (step === 'journey') {
    return <Step1Journey answers={answers} setAnswer={setAnswer} onContinue={onContinue} />;
  }
  if (step === 'pace') {
    return <Step5Pace answers={answers} setAnswer={setAnswer} onContinue={onContinue} />;
  }
  if (step === 'parq') {
    return <Step3ParQ answers={answers} setAnswer={setAnswer} onContinue={onContinue} />;
  }
  return <Step4BodyMetrics answers={answers} setAnswer={setAnswer} onContinue={onContinue} />;
}

/**
 * Step dispatcher — renders the correct step component for the
 * current step number. Steps not yet implemented show a placeholder
 * with a note about which commit will land them.
 */
function renderStep(
  step: StepNumber,
  answers: OnboardingAnswers,
  setAnswer: (patch: Partial<OnboardingAnswers>) => void,
  onContinue: () => void,
  onCoachPath?: () => void,
  /** B45 — step 6's forward-CTA label, resolved from the queued beats. */
  step6CtaLabel?: string,
  /** O3 — the reveal's "Change my answers" link. Re-enters the wizard; never
   *  persists (the completion POST rides the dashboard CTA inside Step 7). */
  onChangeAnswers?: () => void,
  /** B108 — where the reveal sends an athlete whose PAR-Q screening is
   *  incomplete: the screening step, with no completion write. */
  onScreeningIncomplete?: () => void,
  /** Round-21 critic F2 — true when step 3 is being shown because the reveal
   *  bounced her here, which is the only case that gets the arrival line.
   *  Round-31 UX D2 — it survives the return hop to step 7, where it means
   *  "she has already watched the staging" and settles the reveal. */
  arrivedFromReveal?: boolean,
) {
  if (step === 1) {
    return (
      <Step1Journey
        answers={answers}
        setAnswer={setAnswer}
        onContinue={onContinue}
        onCoachPath={onCoachPath}
      />
    );
  }

  if (step === 2) {
    return (
      <Step2Location
        answers={answers}
        setAnswer={setAnswer}
        onContinue={onContinue}
      />
    );
  }

  if (step === 3) {
    return (
      <Step3ParQ
        answers={answers}
        setAnswer={setAnswer}
        onContinue={onContinue}
        arrivedFromReveal={arrivedFromReveal}
      />
    );
  }

  if (step === 4) {
    return (
      <Step4BodyMetrics
        answers={answers}
        setAnswer={setAnswer}
        onContinue={onContinue}
      />
    );
  }

  if (step === 5) {
    return (
      <Step5Pace
        answers={answers}
        setAnswer={setAnswer}
        onContinue={onContinue}
      />
    );
  }

  if (step === 6) {
    return (
      <Step6Focus
        answers={answers}
        setAnswer={setAnswer}
        onContinue={onContinue}
        ctaLabel={step6CtaLabel}
      />
    );
  }

  if (step === 7) {
    return (
      <Step7Welcome
        answers={answers}
        onChangeAnswers={onChangeAnswers}
        onScreeningIncomplete={onScreeningIncomplete}
        // Round-31 UX D2 — on step 7 this flag is only ever set on the return
        // leg of the B108 bounce, so it reads: open on the settled frame she
        // left, rather than replaying 3.5s of "Reading your answers" over the
        // answers she just gave. A first arrival has it false and stages.
        settled={arrivedFromReveal}
      />
    );
  }

  // Defensive fallback — should never render; all 7 steps are wired.
  return (
    <div style={{ padding: '12px 0' }}>
      <SerifHeading level="md" as="div">
        Unknown step
      </SerifHeading>
    </div>
  );
}
